import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Home as HomeIcon, Snowflake, Building2, Layers, Wrench, Droplets, Zap, Palette, Camera, Sprout, Flame } from "lucide-react";
import { SERVICES as TIDS } from "@/constants/testIds";
import { SERVICES } from "@/config/site";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

const iconFor = (slug) => {
  const map = {
    "renovation-generale": HomeIcon,
    climatisation: Snowflake,
    "poele-granules": Flame,
    facade: Building2,
    toiture: Layers,
    isolation: Layers,
    peinture: Palette,
    plomberie: Droplets,
    electricite: Zap,
    surveillance: Camera,
    arrosage: Sprout,
  };
  return map[slug] || Wrench;
};

export default function ServicesPage() {
  useSEO({
    title: "Nos services — Climatisation, PAC, façade, toiture & isolation",
    description:
      "PENOW propose la mise en relation avec des professionnels partenaires spécialisés en climatisation, PAC, poêle à granulés, ravalement de façade, nettoyage et rénovation de toiture, isolation, peinture, plomberie, électricité, caméras de surveillance et arrosage automatique à Fondettes et en Indre-et-Loire. Réseau d'artisans partenaires certifiés RGE.",
    keywords:
      "installation climatisation Fondettes, pose PAC Tours, ravalement façade Indre-et-Loire, démoussage toiture 37, isolation combles Fondettes, ITE isolation extérieure Tours, réseau artisans partenaires RGE QualiPAC, mise en relation tous corps d'état, caméra surveillance 37, arrosage automatique Fondettes",
    path: "/services",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: SERVICES.map((s, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        item: {
          "@type": "Service",
          name: s.title,
          description: s.description,
          provider: {
            "@type": "LocalBusiness",
            name: "PENOW",
            telephone: "+33688213971",
            areaServed: "Indre-et-Loire",
          },
          areaServed: [
            { "@type": "City", "name": "Fondettes" },
            { "@type": "City", "name": "Tours" },
            { "@type": "AdministrativeArea", "name": "Indre-et-Loire" },
          ],
        },
      })),
    },
  });
  return (
    <div data-testid={TIDS.section}>
      {/* Header */}
      <section className="container-editorial pt-16 md:pt-24 pb-10">
        <div className="max-w-3xl">
          <div className="eyebrow mb-6">Nos services de mise en relation</div>
          <h1 className="font-display text-5xl md:text-6xl leading-tight text-anthracite">
            Un réseau, tous les métiers.
          </h1>
          <p className="mt-6 text-lg text-anthracite/70 leading-relaxed">
            De la rénovation complète au geste technique, PENOW vous connecte
            avec des professionnels partenaires qualifiés pour chaque corps de
            métier, avec un seul interlocuteur et un devis clair.
          </p>
          <p className="mt-4 text-xs text-anthracite/50 italic">
            PENOW est une plateforme de mise en relation. Les travaux sont réalisés
            par nos professionnels partenaires sous leur propre responsabilité.
          </p>
        </div>
      </section>

      {/* Services grid — icon-first, no placeholders */}
      <section className="container-editorial pb-20 md:pb-28">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SERVICES.map((s, i) => {
            const Icon = iconFor(s.slug);
            const highlighted = s.featured;
            return (
              <Reveal
                key={s.slug}
                delay={(i % 4) * 80}
                className={`group relative bg-white border ${
                  highlighted ? "border-terracotta" : "border-black/10"
                } hover:border-copper transition-colors p-8 md:p-10 flex flex-col`}
              >
                {highlighted && (
                  <div className="absolute -top-3 left-8 bg-terracotta text-white text-[10px] uppercase tracking-[0.28em] px-3 py-1">
                    Offre du moment
                  </div>
                )}

                <div className="flex items-start justify-between gap-6">
                  <div
                    className={`w-16 h-16 flex items-center justify-center transition-colors ${
                      highlighted
                        ? "bg-terracotta text-white"
                        : "bg-terracotta/10 text-terracotta group-hover:bg-terracotta group-hover:text-white"
                    }`}
                  >
                    <Icon className="w-8 h-8" strokeWidth={1.25} />
                  </div>
                  <div className="font-display text-4xl text-anthracite/10 leading-none">
                    0{i + 1}
                  </div>
                </div>

                <div className="mt-6 text-[10px] uppercase tracking-[0.28em] text-anthracite/50">
                  {s.short}
                </div>
                <h2
                  data-testid={TIDS.card}
                  className="mt-2 font-display text-2xl md:text-3xl text-anthracite leading-tight"
                >
                  {s.title}
                </h2>
                <p className="mt-4 text-anthracite/70 leading-relaxed">
                  {s.description}
                </p>

                <ul className="mt-6 space-y-2">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-anthracite">
                      <Check className="w-4 h-4 text-terracotta mt-0.5 shrink-0" strokeWidth={2} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 pt-6 border-t border-black/5 flex items-center justify-between">
                  <Link
                    to="/contact"
                    data-testid={TIDS.quoteBtn}
                    className={`inline-flex items-center gap-2 text-sm tracking-wide transition-colors ${
                      highlighted
                        ? "text-terracotta hover:text-copper"
                        : "text-anthracite hover:text-terracotta"
                    }`}
                  >
                    Demander un devis
                    <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                  </Link>
                  <Link
                    to="/realisations"
                    className="text-[11px] uppercase tracking-[0.22em] text-anthracite/40 hover:text-terracotta transition-colors"
                  >
                    Voir des exemples
                  </Link>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Bottom CTA band */}
        <div className="mt-16 bg-deep-blue text-white p-10 md:p-14 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <div className="eyebrow text-terracotta mb-3">Besoin conseil ?</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-xl">
              Un doute sur le type de travaux à envisager ?
              <br />
              <span className="text-white/60">Nous vous conseillons gratuitement.</span>
            </h2>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-terracotta hover:bg-copper text-white px-6 py-4 tracking-wide transition-colors self-start md:self-auto"
            data-testid="services-cta-contact"
          >
            Nous contacter
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
}
